import React, { useEffect, useState, useCallback } from "react";
import { Box, Flex, Text, Button, HStack } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { getIdToken } from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../config/firebase";

const Navbar: React.FC = () => {
  const { currentUser, forceAuthCheck, signInWithGoogle, logout } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Create memoized version of refreshIdToken to avoid dependency issues
  const refreshIdToken = useCallback(async () => {
    if (currentUser) {
      try {
        setIsRefreshing(true);
        const token = await getIdToken(currentUser, true);
        console.log("Token refreshed:", {
          tokenPrefix: token.substring(0, 10) + "...",
          timestamp: new Date().toISOString(),
          uid: currentUser.uid,
        });
        return token;
      } catch (error) {
        console.error("Error refreshing token:", error);
        return null;
      } finally {
        setIsRefreshing(false);
      }
    }
    return null;
  }, [currentUser]);

  // Memoize handleSignIn to avoid dependency issues - MOVED UP BEFORE useEffect
  const handleSignIn = useCallback(async () => {
    // If already signed in, just refresh the token
    if (currentUser) {
      console.log("NAVBAR: User already signed in, refreshing token");
      await refreshIdToken();
      return; // Don't reload the page here
    }

    setIsSigningIn(true);
    try {
      console.log("NAVBAR: Starting sign in process");
      await signInWithGoogle();
      console.log("NAVBAR: Sign in completed");
      // Don't reload the page - let the auth state listener handle the update
    } catch (error: any) {
      // If error is related to popup being blocked or closed, don't show error
      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/popup-blocked" ||
        error.message?.includes("The popup has been closed")
      ) {
        console.log("NAVBAR: Sign-in popup was closed or blocked");
      } else {
        console.error("NAVBAR: Error signing in:", {
          code: error.code,
          message: error.message,
        });
      }
      throw error; // Rethrow so callers can handle it if needed
    } finally {
      setIsSigningIn(false);
    }
  }, [currentUser, refreshIdToken, signInWithGoogle]);

  useEffect(() => {
    // Log authentication state
    console.log("NAVBAR: Auth state changed:", {
      isAuthenticated: !!currentUser,
      uid: currentUser?.uid,
      email: currentUser?.email,
      displayName: currentUser?.displayName,
      timestamp: new Date().toISOString(),
    });

    // Force token refresh on initial load
    if (currentUser) {
      refreshIdToken();
    }
  }, [currentUser, refreshIdToken]);

  // Component mount/unmount tracking
  useEffect(() => {
    console.log("NAVBAR: Component mounted at", new Date().toISOString());

    return () => {
      console.log("NAVBAR: Component unmounted at", new Date().toISOString());
    };
  }, []);

  const checkAndRefreshAuth = async () => {
    setIsRefreshing(true);
    try {
      console.log("NAVBAR: Manually checking auth state...");
      const user = await forceAuthCheck();

      if (user) {
        console.log("NAVBAR: Auth check successful, user is authenticated");
        // Force a page reload to update UI with auth state
        window.location.reload();
      } else {
        console.log("NAVBAR: Auth check complete, no user found");
      }
    } catch (error) {
      console.error("NAVBAR: Error in manual auth check:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      console.log("NAVBAR: Attempting sign out...");
      await logout();
      // No need for redirection here as the logout function handles it
    } catch (error) {
      console.error("NAVBAR: Error signing out:", error);
    }
  };

  return (
    <Box>
      <Flex
        bg="white"
        color="gray.600"
        minH={"60px"}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={"solid"}
        borderColor="gray.200"
        align={"center"}
        justify={"space-between"}
      >
        <Flex flex={{ base: 1 }} justify={{ base: "center", md: "start" }}>
          <Text
            textAlign={{ base: "center", md: "left" }}
            fontFamily={"heading"}
            color="blue.600"
            fontWeight="bold"
            fontSize="xl"
          >
            MedBooking
          </Text>
        </Flex>

        <HStack flex={{ base: 1, md: 0 }} justify={"flex-end"} gap={6}>
          <Link to="/">
            <Box px={2} py={1} borderRadius="md" _hover={{ bg: "gray.100" }}>
              Book Appointment
            </Box>
          </Link>

          <Link to="/appointments">
            <Box px={2} py={1} borderRadius="md" _hover={{ bg: "gray.100" }}>
              My Appointments
            </Box>
          </Link>

          {currentUser ? (
            <>
              <Text>Welcome, {currentUser.displayName}</Text>
              <Button
                onClick={handleSignOut}
                fontSize={"sm"}
                fontWeight={400}
                variant="ghost"
              >
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={currentUser ? checkAndRefreshAuth : handleSignIn}
                fontSize={"sm"}
                fontWeight={400}
                variant="ghost"
                isLoading={isSigningIn || isRefreshing}
                loadingText={currentUser ? "Refreshing" : "Signing In"}
              >
                {currentUser ? "Refresh Auth" : "Sign In"}
              </Button>
              {!currentUser && (
                <Button
                  onClick={handleSignIn}
                  display={{ base: "none", md: "inline-flex" }}
                  fontSize={"sm"}
                  fontWeight={600}
                  color={"white"}
                  bg={"blue.500"}
                  _hover={{
                    bg: "blue.400",
                  }}
                >
                  Sign Up
                </Button>
              )}
            </>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

export default Navbar;
